import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  MouseSensor,
  useSensor,
  useSensors,
  DropAnimation,
  pointerWithin,
} from "@dnd-kit/core";
import React from "react";

import { FSType } from "@/shared/domain/files/FileRecord";
import {
  FileSystemNode,
  DirectoryNode,
  isFile,
  isDirectory,
} from "@/shared/domain/files/filesystem";
import { useBulkMoveNodesMutation } from "@/shared/domain/files/hooks/useBulkMoveNodesMutation";
import { MoveNodeOperation } from "@/shared/domain/files/operations";
import { RobotoDomainException } from "@/shared/http";

import { FileBrowserAction, FileBrowserActionTypes } from "./actions";
import { FileDragOverlay } from "./FileDragOverlay";

interface FileBrowserDndContextProps {
  children: React.ReactNode;
  dispatch: React.Dispatch<FileBrowserAction>;
  selectedItems: FileSystemNode[];
  onError: (message: string, error?: Error) => void;
  onSuccess: (message: string) => void;
  onValidDrop: (event: DragEndEvent, fs_nodes: FileSystemNode[]) => void;
}

export const FileBrowserDndContext: React.FC<FileBrowserDndContextProps> = ({
  children,
  dispatch,
  selectedItems,
  onError,
  onSuccess,
  onValidDrop,
}) => {
  // sensors allow us to determine when a drag is starting, which allows clicks, double clicks on rows/grid items to work correctly
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(MouseSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  const [activeDraggable, setActiveDraggable] =
    React.useState<FileSystemNode | null>(null);
  const [shouldAnimateDrop, setShouldAnimateDrop] = React.useState(true);

  const bulkMoveNodesMutation = useBulkMoveNodesMutation();

  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current?.data as FileSystemNode;
    setActiveDraggable(activeData);

    if (!isActiveDraggableInSelectedItems(activeData, selectedItems)) {
      dispatch({
        type: FileBrowserActionTypes.ClearSelectedItems,
        payload: null,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.over) {
      if (event.active.id === event.over.id) {
        setShouldAnimateDrop(true);
        return;
      }

      const eventData = event.active.data.current?.data as FileSystemNode;
      const hoverData = event.over.data.current?.data as DirectoryNode;

      const shouldUseSelectedItems = isActiveDraggableInSelectedItems(
        eventData,
        selectedItems,
      );

      if (
        isDropZoneInSelectedItems(
          hoverData.directory.directory_id,
          selectedItems,
        )
      ) {
        setShouldAnimateDrop(true);
        return;
      }

      let nodesToMove: FileSystemNode[] = [];

      if (shouldUseSelectedItems) {
        nodesToMove = selectedItems;
      } else {
        nodesToMove = [eventData];
      }

      onValidDrop(event, nodesToMove);

      setShouldAnimateDrop(false);

      const operations: MoveNodeOperation[] = [];

      for (const node of nodesToMove) {
        if (isFile(node)) {
          operations.push({
            node_id: node.file.file_id,
            fs_type: FSType.File,
            new_parent_id: hoverData.directory.directory_id,
            new_name: node.name,
          });
        } else if (isDirectory(node)) {
          operations.push({
            node_id: node.directory.directory_id,
            fs_type: FSType.Directory,
            new_parent_id: hoverData.directory.directory_id,
            new_name: node.name,
          });
        }
      }

      bulkMoveNodesMutation
        .mutateAsync({
          datasetId: hoverData.directory.association_id,
          operations,
        })
        .then(() => {
          dispatch({
            type: FileBrowserActionTypes.ClearSelectedItems,
            payload: null,
          });

          onSuccess("Move complete");
        })
        .catch((error) => {
          if (error instanceof RobotoDomainException && error.isClientError()) {
            onError(error.message, error);
            return;
          }

          onError(
            "Unable to move items. Please try again.",
            error instanceof Error ? error : new Error(String(error)),
          );
        });
    } else {
      setShouldAnimateDrop(true);
    }

    setActiveDraggable(null);
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={pointerWithin}
    >
      {children}

      <DragOverlay dropAnimation={shouldAnimateDrop ? dropAnimation : null}>
        {activeDraggable?.name && (
          <FileDragOverlay
            activeDraggable={activeDraggable}
            itemCount={selectedItems.length}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

const dropAnimation: DropAnimation = {
  duration: 300,
  easing: "ease-out",
  keyframes({
    transform,
  }: {
    transform: { initial: { x: number; y: number } };
  }) {
    const correctedStart = `translate(${transform.initial.x}px, ${transform.initial.y}px) `;
    // the overlay returns to the origin, plus the nudge amount calculated inside the overlay component
    const correctedEnd = `translate(0, 0)`;

    return [
      { transform: correctedStart, opacity: 1 },
      { transform: correctedEnd, opacity: 1 },
    ];
  },
  sideEffects: null,
};

const isActiveDraggableInSelectedItems = (
  activeDraggable: FileSystemNode,
  selectedItems: FileSystemNode[],
) => {
  return selectedItems.find((item) => {
    if (isFile(item)) {
      if (isFile(activeDraggable)) {
        return item.file.file_id === activeDraggable.file.file_id;
      } else {
        return false;
      }
    } else if (isDirectory(item)) {
      if (isDirectory(activeDraggable)) {
        return (
          item.directory.relative_path ===
          activeDraggable.directory.relative_path
        );
      } else {
        return false;
      }
    }
    return false;
  });
};

const isDropZoneInSelectedItems = (
  dropZoneId: string,
  selectedItems: FileSystemNode[],
) => {
  return selectedItems.find((item) => {
    if (isDirectory(item)) {
      return item.directory.directory_id === dropZoneId;
    }
    return false;
  });
};
